import { createDarkTheme, createLightTheme, type BrandVariants, type Theme } from '@fluentui/react-components'

/**
 * Libris blue. Anchored on #0B5CFF — the value used by the brand mark — and
 * extended into a full Fluent 2 ramp. Dark surfaces draw the accent from the
 * 100/110 steps so it keeps contrast against ink.
 */
const libris: BrandVariants = {
  10: '#01040F',
  20: '#061131',
  30: '#091B52',
  40: '#0A2470',
  50: '#0A2E92',
  60: '#0838B5',
  70: '#0946D9',
  80: '#0B5CFF',
  90: '#2E72FF',
  100: '#4C87FF',
  110: '#6699FF',
  120: '#80ABFF',
  130: '#9ABDFF',
  140: '#B5CFFF',
  150: '#D3E1FF',
  160: '#EDF2FF',
}

const display = '"Segoe UI Variable Display","Segoe UI Variable Static Display","Segoe UI",system-ui,sans-serif'
const text = '"Segoe UI Variable Text","Segoe UI Variable Static Text","Segoe UI",system-ui,sans-serif'
const mono = '"Cascadia Code","Cascadia Mono",ui-monospace,"SF Mono",Consolas,monospace'

/** Shared shape + typography personality: squarer corners than stock Fluent, tighter display type. */
const shared = {
  fontFamilyBase: text,
  fontFamilyMonospace: mono,
  fontFamilyNumeric: mono,
  borderRadiusNone: '0',
  borderRadiusSmall: '2px',
  borderRadiusMedium: '3px',
  borderRadiusLarge: '4px',
  borderRadiusXLarge: '6px',
  durationFaster: '120ms',
  durationNormal: '220ms',
  durationSlow: '380ms',
  curveEasyEase: 'cubic-bezier(0.33, 0, 0.15, 1)',
  curveDecelerateMid: 'cubic-bezier(0.1, 0.9, 0.2, 1)',
}

const base = createLightTheme(libris)
const baseDark = createDarkTheme(libris)

export const librisLightTheme: Theme = {
  ...base,
  ...shared,
  colorNeutralBackground1: '#FFFFFF',
  colorNeutralBackground2: '#F7F7F5',
  colorNeutralBackground3: '#F1F1EE',
  colorNeutralBackground1Hover: '#F7F7F5',
  colorNeutralBackground1Pressed: '#EDEDEA',
  colorNeutralForeground1: '#14202B',
  colorNeutralForeground2: '#3D4C59',
  colorNeutralForeground3: '#5D6B78',
  colorNeutralStroke1: '#D9D9D3',
  colorNeutralStroke2: '#E6E6E1',
  colorSubtleBackgroundHover: '#F1F1EE',
}

export const librisDarkTheme: Theme = {
  ...baseDark,
  ...shared,
  colorNeutralBackground1: '#0D141B',
  colorNeutralBackground2: '#111A23',
  colorNeutralBackground3: '#16212C',
  colorNeutralBackground1Hover: '#16212C',
  colorNeutralBackground1Pressed: '#0A1017',
  colorNeutralBackground4: '#080D12',
  colorNeutralForeground1: '#E8EDF2',
  colorNeutralForeground2: '#B8C4D0',
  colorNeutralForeground3: '#8B99A7',
  colorNeutralStroke1: '#26333F',
  colorNeutralStroke2: '#1B2530',
  colorSubtleBackgroundHover: '#16212C',
}

export const fontStacks = { display, text, mono }
